import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot,
  faCheckCircle,
  faLaptop,
  faGavel,
  faFolderOpen,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import './BdgCurso.css';
import logoBdg from '../../images/bdg/logo-bdg.png';
import cursoImg from '../../images/bdg/curso-ia-derecho.jpg';
import heroFondo from '../../images/bdg/hero-fondo.jpg';
import temisLogo from '../../images/logoMain-D.png';

const REGISTRO_URL =
  'https://forms.cloud.microsoft/Pages/ResponsePage.aspx?id=DQSIkWdsW0yxEjajBLZtrQAAAAAAAAAAAAZAALnPKJdUMTZFMk1RSTNOMEEyWUhCWDhGUFBMN0dGQy4u';
const BROCHURE_URL =
  'https://drive.google.com/file/d/1A6meMthVJ6g6KNTmy7Hs4p6nzSSmIsmq/view?usp=sharing';

/* Resalte con bloque de color sesgado: el gesto de marca de Temis. */
const Slab = ({ color = 'yellow', children }) => (
  <span className={`bdg-slab bdg-slab-${color}`}>
    <span>{children}</span>
  </span>
);

const aprendizajes = [
  {
    icon: faRobot,
    color: 'yellow',
    titulo: 'Comprender la IA generativa',
    detalle: 'y sus posibilidades y limitaciones en el ámbito jurídico.',
  },
  {
    icon: faCheckCircle,
    color: 'blue',
    titulo: 'Utilizar Claude de forma segura',
    detalle: 'protegiendo la confidencialidad y el secreto profesional.',
  },
  {
    icon: faLaptop,
    color: 'red',
    titulo: 'Crear prompts jurídicos efectivos',
    detalle: 'para obtener mejores resultados.',
  },
  {
    icon: faGavel,
    color: 'yellow',
    titulo: 'Apoyar la investigación y el análisis jurídico',
    detalle: 'verificando siempre las fuentes.',
  },
  {
    icon: faFolderOpen,
    color: 'blue',
    titulo: 'Organizar expedientes y procesos de litigio',
    detalle: 'estructurando hechos, pruebas y argumentos.',
  },
  {
    icon: faExclamationTriangle,
    color: 'red',
    titulo: 'Identificar riesgos, errores y alucinaciones',
    detalle: 'generadas por la IA.',
  },
];

const practicas = [
  {
    tag: 'Contratos',
    color: 'blue',
    texto:
      'Revisión de cláusulas, riesgos, obligaciones y redacción de documentos.',
  },
  {
    tag: 'Litigio',
    color: 'red',
    texto: 'Cronologías, matrices probatorias y construcción de teorías del caso.',
  },
  {
    tag: 'Comunicaciones',
    color: 'yellow',
    texto:
      'Análisis de correos, identificación de pendientes y preparación de respuestas.',
  },
  {
    tag: 'Criterios jurídicos',
    color: 'blue',
    texto:
      'Resúmenes ejecutivos, matrices técnicas y borradores de opiniones legales.',
  },
  {
    tag: 'Seguridad',
    color: 'red',
    texto:
      'Decidir qué información puede compartirse con IA y bajo qué configuración.',
  },
];

const BdgCurso = () => {
  useEffect(() => {
    const tituloPrevio = document.title;
    document.title = 'Curso IA Aplicada al Derecho - Business Development Group';
    window.scrollTo(0, 0);
    return () => {
      document.title = tituloPrevio;
    };
  }, []);

  return (
    <div className="bdg-page">
      {/* ===== HERO ===== */}
      <section
        className="bdg-hero"
        style={{ backgroundImage: `url(${heroFondo})` }}
      >
        <div className="bdg-hero-overlay" />
        <div className="bdg-container bdg-hero-inner">
          <div className="bdgxtemis">
            <img
              src={logoBdg}
              alt="Business Development Group"
              className="bdg-hero-logo"
            />
            <span className="bdgxtemis-x" aria-hidden="true">
              ×
            </span>
            <img src={temisLogo} alt="Temis" className="temis-hero-logo" />
          </div>
          <p className="bdg-kicker">
            <span className="bdg-chip bdg-chip-red">
              <span>Curso online</span>
            </span>
          </p>
          <h1 className="bdg-hero-title">
            IA aplicada al Derecho:
            <span>Inteligencia Artificial para el Ejercicio Legal</span>
          </h1>
          <p className="bdg-hero-sub">
            Transforma la manera en que trabajas con IA, sin comprometer la
            confidencialidad de tus clientes.
          </p>
          <p className="bdg-hero-fecha">
            Inicio <Slab color="blue">10 de noviembre</Slab>
          </p>
          <a
            className="bdg-btn bdg-btn-yellow"
            href={REGISTRO_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>Regístrate ahora</span>
          </a>
        </div>
      </section>

      {/* ===== POR QUÉ TOMAR ESTE CURSO ===== */}
      <section className="bdg-section bdg-section-dark">
        <div className="bdg-container bdg-narrow">
          <h2 className="bdg-h2 bdg-h2-light">
            ¿Por qué tomar <Slab color="red">este curso?</Slab>
          </h2>
          <p className="bdg-lead">
            La Inteligencia Artificial ya forma parte de la transformación de la
            profesión legal.
          </p>
          <p className="bdg-body">
            El reto no es solamente aprender a utilizar una herramienta, sino
            saber cómo utilizarla correctamente, qué información compartir, cómo
            proteger los datos de los clientes y cómo verificar los resultados
            antes de incorporarlos a tu trabajo profesional.
          </p>
          <p className="bdg-body">
            Este programa te permitirá desarrollar un uso de la IA seguro,
            eficiente y adaptado al ejercicio jurídico.
          </p>
        </div>
      </section>

      {/* ===== QUÉ APRENDERÁS ===== */}
      <section className="bdg-section bdg-section-light">
        <div className="bdg-container">
          <h2 className="bdg-h2 bdg-h2-dark">
            ¿Qué aprenderás con este curso?
          </h2>
          <ul className="bdg-grid">
            {aprendizajes.map((item) => (
              <li key={item.titulo} className="bdg-card">
                <span className={`bdg-icon-chip bdg-icon-chip-${item.color}`}>
                  <FontAwesomeIcon icon={item.icon} />
                </span>
                <h3 className="bdg-card-title">{item.titulo}</h3>
                <p className="bdg-card-text">{item.detalle}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ===== DE LA TEORÍA A LA PRÁCTICA ===== */}
      <section className="bdg-section bdg-section-light bdg-section-practica">
        <div className="bdg-container bdg-narrow">
          <h2 className="bdg-h2 bdg-h2-dark">
            De la teoría a la <Slab color="yellow">práctica</Slab>
          </h2>
          <p className="bdg-body bdg-center">
            Durante el curso trabajarás con situaciones similares a las que
            enfrenta diariamente una firma de abogados:
          </p>
          <ul className="bdg-practicas">
            {practicas.map((item) => (
              <li key={item.tag}>
                <span className={`bdg-tag bdg-tag-${item.color}`}>
                  <span>{item.tag}</span>
                </span>
                <span className="bdg-practica-texto">{item.texto}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ===== DETALLES DEL CURSO ===== */}
      <section className="bdg-section bdg-section-dark bdg-center">
        <div className="bdg-container bdg-narrow">
          <h2 className="bdg-h2 bdg-h2-light">Curso online en vivo</h2>
          <p className="bdg-highlight">
            <Slab color="yellow">10 sesiones · 100% práctico</Slab>
          </p>
          <div className="bdg-detalles-fila">
            <div className="bdg-datos">
              <p className="bdg-detalle">
                <span>Inicio</span>22 de octubre
              </p>
              <p className="bdg-detalle">
                <span>Horario</span>5:30 p. m. – 7:30 p. m. (Martes y Jueves)
              </p>
            </div>
            <a
              className="bdg-btn bdg-btn-blue"
              href={BROCHURE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>Ver brochure informativo</span>
            </a>
          </div>
        </div>
      </section>

      {/* ===== CIERRE ===== */}
      <section className="bdg-section bdg-cta">
        <div className="bdg-container bdg-cta-inner">
          <div className="bdg-cta-texto">
            <h2 className="bdg-cta-title">
              Lleva la IA a <Slab color="red">tu práctica legal.</Slab>
            </h2>
            <p className="bdg-cta-body">
              Aprende a utilizar la Inteligencia Artificial como una herramienta
              de apoyo para analizar, organizar, redactar y revisar, sin delegar
              el criterio profesional.
            </p>
            <a
              className="bdg-btn bdg-btn-yellow"
              href={REGISTRO_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>Regístrate ahora</span>
            </a>
          </div>
          <div className="bdg-cta-media">
            <img
              src={cursoImg}
              alt="Curso IA aplicada al Derecho: inicio 10 de noviembre"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bdg-footer">
        <div className="bdg-container bdg-footer-inner">
          <div className="bdgxtemis bdgxtemis-footer">
            <img src={logoBdg} alt="Business Development Group" />
            <span className="bdgxtemis-x" aria-hidden="true">
              ×
            </span>
            <img src={temisLogo} alt="Temis" />
          </div>
          <a href="https://bdgsa.net" target="_blank" rel="noopener noreferrer">
            www.bdgsa.net
          </a>
        </div>
      </footer>
    </div>
  );
};

export default BdgCurso;
